import { InputAdornment, TextField } from '@one-thd/sui-atomic-components';
import { Calendar } from '@one-thd/sui-icons';
import { DateTime } from 'luxon';
import React, { Dispatch, SetStateAction } from 'react';
import { DateFilterSettings, DateSettings } from '../../../models/order-filter';

interface IOrderFilterInputProps {
  name: string;
  dateType: 'startDate' | 'endDate';
  dateSettings: DateSettings;
  setDateSettings: Dispatch<SetStateAction<DateSettings>>;
  setFilterSettings: Dispatch<SetStateAction<DateFilterSettings>>;
}

export const OrderFilterInput = ({
  name,
  dateType,
  dateSettings,
  setDateSettings,
  setFilterSettings,
}: IOrderFilterInputProps) => {
  const label = { inputProps: { 'aria-label': 'date range' } };

  const currentValue =
    dateSettings?.selectedDate === 'custom'
      ? (dateSettings[dateType] as string) ?? ''
      : '';

  const errorMessage = (() => {
    if (!currentValue || currentValue.length !== 10) return null;
    const parsed = DateTime.fromFormat(currentValue, 'MM/dd/yyyy');
    if (!parsed.isValid) return 'Enter a valid date';
    const maxDate = DateTime.now().startOf('day');
    const minDate = DateTime.now().minus({ months: 84 }).startOf('day');
    if (parsed > maxDate) return 'Date cannot be in the future';
    if (parsed < minDate) return 'Date is outside the allowed range';
    if (dateType === 'endDate' && dateSettings.startDate) {
      const otherValue = dateSettings.startDate.toString();
      if (otherValue.length === 10) {
        const parsedStart = DateTime.fromFormat(otherValue, 'MM/dd/yyyy');
        if (parsedStart.isValid && parsed < parsedStart) {
          return 'End date cannot be before start date';
        }
      }
    }
    if (dateType === 'startDate' && dateSettings.endDate) {
      const otherValue = dateSettings.endDate.toString();
      if (otherValue.length === 10) {
        const parsedEnd = DateTime.fromFormat(otherValue, 'MM/dd/yyyy');
        if (parsedEnd.isValid && parsed > parsedEnd) {
          return 'Start date cannot be after end date';
        }
      }
    }
    return null;
  })();

  return (
    <TextField
      placeholder="MM/DD/YYYY"
      id="basic-input"
      fullWidth
      status={errorMessage ? 'error' : undefined}
      statusMessage={errorMessage ?? undefined}
      value={currentValue}
      label={name}
      onChange={(event: { target: { value: string } }) => {
        let inputDate = event.target.value.replace(/[^0-9/]/g, '');

        if (inputDate.length > 10) {
          inputDate = inputDate.slice(0, 10);
        }

        if (
          inputDate.length === 2 &&
          dateSettings[dateType]?.toString().length !== 3
        ) {
          inputDate += '/';
        } else if (
          inputDate.length === 5 &&
          dateSettings[dateType]?.toString().length !== 6
        ) {
          inputDate += '/';
        }

        setDateSettings((prevState) => ({
          ...prevState,
          [dateType]: inputDate,
          selectedDate: 'custom',
        }));

        if (inputDate.length === 10) {
          const parsedDate = DateTime.fromFormat(inputDate, 'MM/dd/yyyy');

          const month = parsedDate.month;
          const day = parsedDate.day;
          const year = parsedDate.year;

          const monthString = month < 10 ? `0${month}` : `${month}`;
          const dayString = day < 10 ? `0${day}` : `${day}`;

          const maxDate = DateTime.now().startOf('day');
          const minDate = DateTime.now().minus({ months: 84 }).startOf('day');
          const invalidKey =
            dateType === 'startDate' ? 'invalidStartDate' : 'invalidEndDate';
          const otherDateStr =
            dateType === 'endDate'
              ? dateSettings.startDate?.toString()
              : dateSettings.endDate?.toString();
          const parsedOther =
            otherDateStr?.length === 10
              ? DateTime.fromFormat(otherDateStr, 'MM/dd/yyyy')
              : null;
          const isOutOfOrder =
            parsedOther?.isValid === true &&
            (dateType === 'endDate'
              ? parsedDate < parsedOther
              : parsedDate > parsedOther);
          const isInvalid =
            !parsedDate.isValid ||
            parsedDate > maxDate ||
            parsedDate < minDate ||
            isOutOfOrder;
          setFilterSettings((prevState) => ({
            ...prevState,
            [invalidKey]: isInvalid,
          }));

          if (parsedDate.isValid && !isInvalid) {
            setFilterSettings((prevState) => ({
              ...prevState,
              customDateUsed: true,
            }));
            setDateSettings((prevState) => ({
              ...prevState,
              selectedDate: 'custom',
              [dateType]: `${monthString}/${dayString}/${year}`,
            }));
          }
        } else {
          const invalidKey =
            dateType === 'startDate' ? 'invalidStartDate' : 'invalidEndDate';
          setFilterSettings((prevState) => ({
            ...prevState,
            [invalidKey]: false,
          }));
        }
        if (inputDate.length > 0) {
          setFilterSettings((prevState) => ({
            ...prevState,
            customDateUsed: true,
          }));
        }
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <span
              className="sui-cursor-pointer sui-flex"
              onClick={() => {
                if (dateType === 'startDate') {
                  setFilterSettings((prevState) => ({
                    ...prevState,
                    showStartCalendar: true,
                  }));
                } else if (dateType === 'endDate') {
                  setFilterSettings((prevState) => ({
                    ...prevState,
                    showEndCalendar: true,
                  }));
                }
              }}
              onKeyDown={() => {
                if (dateType === 'startDate') {
                  setFilterSettings((prevState) => ({
                    ...prevState,
                    showStartCalendar: true,
                  }));
                } else if (dateType === 'endDate') {
                  setFilterSettings((prevState) => ({
                    ...prevState,
                    showEndCalendar: true,
                  }));
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Open date picker"
            >
              <Calendar size="small" />
            </span>
          </InputAdornment>
        ),
        ...label,
      }}
    />
  );
};
